/**
 * Settlement Generator — Main Orchestrator
 * Wires all generation phases together into a single pure function.
 */

import type { Settlement, SettlementConfig, Wall, Gate } from './types';
import { SeededRandom } from './random';
import { placeDistricts, placeBuildings, computeBounds } from './layout';
import { generateEntryPoints, generateRoads } from './roads';
import { generateWalls } from './walls';
import { generateWater } from './water';
import { TIER_TEMPLATES } from './templates';

// ============================================================================
// MAIN GENERATOR
// ============================================================================

/**
 * Generate a complete settlement layout.
 * Pure function: same seed + config → identical output.
 */
export function generateSettlement(config: SettlementConfig): Settlement {
  const rng = new SeededRandom(config.seed);
  const tierTemplate = TIER_TEMPLATES[config.tier];

  // Phase 1 + 2: District placement with organic polygon boundaries
  const districts = placeDistricts(config, rng);

  // Phase 3: Building scatter within districts
  const buildings = placeBuildings(config, districts, rng);

  // Phase 4a: Entry points
  const entryPoints = generateEntryPoints(config, districts, rng);

  // Phase 4b: Road network
  const roads = generateRoads(config, districts, buildings, entryPoints, rng);

  // Phase 5: Walls & gates (towns/cities)
  let walls: Wall[] = [];
  let gates: Gate[] = [];
  if (tierTemplate.hasWalls) {
    const wallResult = generateWalls(districts, entryPoints, tierTemplate.hasInnerWall, rng);
    walls = wallResult.walls;
    gates = wallResult.gates;
  }

  // Phase 6: Water features
  const waterFeatures = generateWater(config.terrain, districts, rng);

  // Compute bounds from all placed elements
  const allExtraPoints = [
    ...entryPoints.map(e => e.position),
    ...roads.flatMap(r => r.points),
    ...walls.flatMap(w => w.points),
    ...waterFeatures.flatMap(w => w.points),
  ];
  const bounds = computeBounds(districts, allExtraPoints);

  // Generate settlement placeholder name based on tier
  const tierNames: Record<string, string[]> = {
    hamlet: ['The Hamlet', 'The Homestead', 'The Settlement'],
    village: ['The Village', 'The Township', 'The Crossroads'],
    town: ['The Town', 'The Borough', 'The Market Town'],
    city: ['The City', 'The Free City', 'The Metropolis'],
  };
  const placeholderName = rng.pick(tierNames[config.tier]);

  return {
    id: `settlement_${config.seed}`,
    seed: config.seed,
    tier: config.tier,
    placeholderName,
    flavoredName: null,
    bounds,
    districts,
    buildings,
    roads,
    walls,
    gates,
    waterFeatures,
    entryPoints,
  };
}
